import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Building2, BookOpen, FileText, Package, BarChart3,
  LogOut, Calculator, Search, ChevronDown, ShieldCheck, Landmark, Users2,
  UserCog, ScrollText, Shirt,
  ClipboardList, Receipt, Bot,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import CommandPalette from "@/components/CommandPalette";
import ShortcutsFooter from "@/components/ShortcutsFooter";
import ShortcutsHelp from "@/components/ShortcutsHelp";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import { SHORTCUTS, isTypingInFormField } from "@/lib/shortcuts";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, key: "F1" },
  { to: "/companies", label: "Companies", icon: Building2, key: "F3" },
  { to: "/ledgers", label: "Ledgers", icon: BookOpen, key: "L" },
  { to: "/vouchers", label: "Vouchers", icon: FileText, key: "V" },
  { to: "/ai-voucher", label: "AI Voucher Entry", icon: Bot, key: "A" },
  { to: "/orders", label: "Orders", icon: ClipboardList, key: "O" },
  { to: "/inventory", label: "Inventory", icon: Package, key: "I" },
  { to: "/styles", label: "Styles", icon: Shirt, key: "T" },
  { to: "/banking", label: "Banking", icon: Landmark, key: "K" },
  { to: "/payroll", label: "Payroll", icon: Users2, key: "Y" },
  { to: "/reports", label: "Reports", icon: BarChart3, key: "R" },
  { to: "/tax", label: "Income Tax", icon: Receipt, key: "X" },
  { to: "/backup", label: "Backup", icon: ShieldCheck, key: "B" },
];

// Build O(1) lookup tables once at module load.
const FKEY_MAP = Object.fromEntries(SHORTCUTS.map((s) => [s.fkey, s.path]));
const ALT_MAP = Object.fromEntries(SHORTCUTS.map((s) => [s.alt, s.path]));

export default function AppShell() {
  const { user, logout } = useAuth();
  const { companies, active, setActive } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      // Cmd/Ctrl+K → Command palette (always allow, even in inputs)
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        e.stopPropagation();
        setPaletteOpen(true);
        return;
      }

      // Skip if typing in form fields
      if (isTypingInFormField(e.target) || isTypingInFormField(document.activeElement)) return;

      // ? → show shortcut help
      if (e.key === "?" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }

      // Alt + digit → reliable cross-browser navigation
      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && ALT_MAP[e.key]) {
        e.preventDefault();
        e.stopPropagation();
        navigate(ALT_MAP[e.key]);
        return;
      }

      // F-keys → Tally-style (may be intercepted by some browsers)
      if (!e.altKey && !e.ctrlKey && !e.metaKey && FKEY_MAP[e.key]) {
        e.preventDefault();
        e.stopPropagation();
        navigate(FKEY_MAP[e.key]);
        return;
      }
    };
    // capture: true → run before browser shortcuts where possible
    window.addEventListener("keydown", handler, { capture: true });
    return () => window.removeEventListener("keydown", handler, { capture: true });
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <SubscriptionBanner />
      {/* Top bar */}
      <header className="h-12 bg-white border-b border-gray-200 flex items-center px-4 gap-4 sticky top-0 z-40">
        <div className="flex items-center gap-2 font-semibold">
          <Calculator className="w-5 h-5 text-blue-700" />
          <span className="tracking-tight">AXIORA</span>
        </div>

        <div className="h-6 w-px bg-gray-200" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="company-switcher"
              className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-gray-50 border border-transparent hover:border-gray-200"
            >
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{active ? active.name : "Select company"}</span>
              <ChevronDown className="w-3 h-3 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel className="text-xs uppercase tracking-wider text-gray-500">
              Companies
            </DropdownMenuLabel>
            {companies.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-gray-500">No companies yet</div>
            )}
            {companies.map((c) => (
              <DropdownMenuItem
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`company-option-${c.id}`}
                className="text-sm"
              >
                <Building2 className="w-3.5 h-3.5 mr-2 text-gray-500" />
                {c.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/companies")} data-testid="manage-companies">
              Manage companies…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <button
          onClick={() => setPaletteOpen(true)}
          data-testid="open-search"
          className="ml-auto flex items-center gap-2 text-sm text-gray-500 border border-gray-200 rounded px-3 h-8 hover:bg-gray-50 w-72"
        >
          <Search className="w-4 h-4" />
          <span>Search ledgers, vouchers, items…</span>
          <kbd className="ml-auto text-[10px] font-mono bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5">⌘K</kbd>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu"
              className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-gray-50"
            >
              <div className="w-7 h-7 rounded-full bg-blue-700 text-white text-xs flex items-center justify-center font-semibold">
                {(user?.name || user?.email || "?")[0]?.toUpperCase()}
              </div>
              <span className="hidden sm:block">{user?.name || user?.email}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Signed in</div>
              <div className="text-sm font-medium mt-0.5">{user?.email}</div>
              <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings/profile")} data-testid="user-menu-profile">
              <UserCog className="w-4 h-4 mr-2" /> Profile & Security
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings/audit-log")} data-testid="user-menu-audit">
              <ScrollText className="w-4 h-4 mr-2" /> Audit Trail
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/subscribe")} data-testid="user-menu-subscribe">
              <ShieldCheck className="w-4 h-4 mr-2" /> My Subscription
            </DropdownMenuItem>
            {user?.is_superadmin && (
              <DropdownMenuItem onClick={() => navigate("/admin/subscriptions")} data-testid="user-menu-admin-subs">
                <ShieldCheck className="w-4 h-4 mr-2 text-purple-700" /> Admin · Subscriptions
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="btn-logout">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="w-56 bg-white border-r border-gray-200 flex-shrink-0 hidden md:block">
          <nav className="py-3">
            {navItems.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={`nav-${it.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2 text-sm border-l-2 ${
                      isActive
                        ? "border-blue-700 bg-blue-50 text-blue-900 font-medium"
                        : "border-transparent text-gray-700 hover:bg-gray-50"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  <span>{it.label}</span>
                  <kbd className="ml-auto text-[10px] font-mono text-gray-400">{it.key}</kbd>
                </NavLink>
              );
            })}
          </nav>
          <div className="px-4 py-3 mt-2 border-t border-gray-100">
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Active FY</p>
            <p className="text-xs text-gray-700 font-mono">
              {active ? `${active.fy_start} → ${active.fy_end}` : "—"}
            </p>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 overflow-auto pb-12">
          <Outlet />
        </main>
      </div>

      <ShortcutsFooter onShowHelp={() => setHelpOpen(true)} />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ShortcutsHelp open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
}
